/**
 * Cached API responses, so the library opens without a network.
 *
 * Only two things need to survive: who is signed in (for the salt) and the
 * encrypted item list. Both are ciphertext or public — the salt is not secret
 * and every payload is sealed — so this cache is no more revealing than the
 * blob cache beside it.
 *
 * The passphrase is not here and never will be. Offline unlock still runs
 * scrypt against what is typed; nothing about being offline weakens that.
 */

const CACHE_NAME = 'peepshow/api/v1';

/**
 * Did the request fail to reach anyone?
 *
 * `fetch` rejects with a TypeError when it cannot connect. An ApiError carries
 * a status, which means a server replied and its answer stands.
 */
function isOffline(error: unknown): boolean {
  const status = (error as { status?: number })?.status;
  return status === undefined || status === 0;
}

async function open(): Promise<Cache | null> {
  if (typeof caches === 'undefined') return null;
  try {
    return await caches.open(CACHE_NAME);
  } catch {
    return null;
  }
}

/**
 * Run `fetcher`, remembering the result; fall back to the last one when
 * offline.
 *
 * Deliberately network-first. A stale library is the fallback, not the
 * default — a video uploaded from the CLI should appear without waiting for a
 * cache to expire.
 */
export async function withOfflineFallback<T>(
  key: string,
  fetcher: () => Promise<T>,
): Promise<T> {
  const cache = await open();
  try {
    const fresh = await fetcher();
    if (cache) {
      await cache.put(
        `/offline/${key}`,
        new Response(JSON.stringify(fresh), {
          headers: { 'Content-Type': 'application/json' },
        }),
      );
    }
    return fresh;
  } catch (error) {
    // Only when the network failed. A server that answered — 401 because the
    // session lapsed, 403, 404 — has told us something, and serving a cached
    // library over the top of "you are not signed in" would be wrong.
    if (!isOffline(error)) throw error;
    const hit = await cache?.match(`/offline/${key}`);
    if (!hit) throw error;
    return (await hit.json()) as T;
  }
}

/** Forget everything remembered for offline use. Called on sign-out. */
export async function clearOfflineCache(): Promise<void> {
  if (typeof caches === 'undefined') return;
  try {
    await caches.delete(CACHE_NAME);
  } catch {
    /* nothing depends on it */
  }
}
